/* eslint-disable no-nested-ternary */
import * as Yup from 'yup';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { enqueueSnackbar } from 'notistack';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm, useWatch } from 'react-hook-form';

import { DatePicker } from '@mui/x-date-pickers';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';

import { paths } from 'src/routes/paths';

import { useAppDispatcher } from 'src/redux/store';
import { updateChequeStatus } from 'src/redux/slices/cheque';

import Label from 'src/components/label';
import FormProvider, { RHFSelect, RHFTextField } from 'src/components/hook-form';

import { ChequeStatus, IChequeItem } from 'src/types/cheque';

interface IUpdateChequeStatus {
  currentCheque: IChequeItem;
  isOpen: boolean;
  onClose: VoidFunction;
}

function getNextAvailableStatuses(currentStatus: ChequeStatus): ChequeStatus[] {
  switch (currentStatus) {
    case ChequeStatus.IN_HAND:
      return [ChequeStatus.PAID];
    case ChequeStatus.PAID:
      return [ChequeStatus.RETURN];
    case ChequeStatus.RETURN:
      return [ChequeStatus.SETTLED];
    case ChequeStatus.SETTLED:
      return [ChequeStatus.HANDOVER];
    case ChequeStatus.HANDOVER:
      return [ChequeStatus.RECOVERED];
    default:
      return [];
  }
}

function UpdateChequeStatus({ currentCheque, isOpen, onClose }: IUpdateChequeStatus) {
  const dispatch = useAppDispatcher();

  const navigate = useNavigate();

  const updateChequeStatusSchema = Yup.object().shape({
    status: Yup.string().required('Status is required'),
    holder: Yup.string().when('status', ([status], schema) =>
      status === ChequeStatus.PAID || status === ChequeStatus.HANDOVER
        ? schema.required('Holder is required')
        : schema.optional()
    ),
    date: Yup.date().required('Return date is required when returning a cheque'),
  });

  const defaultValues = useMemo(
    () => ({
      status: currentCheque.status,
      date: new Date(),
      holder: currentCheque.status === ChequeStatus.SETTLED ? currentCheque.receivedVia : '',
    }),
    [currentCheque]
  );

  const methods = useForm({
    resolver: yupResolver(updateChequeStatusSchema),
    defaultValues,
    mode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
    reset,
  } = methods;

  const watchStatus = useWatch({
    control,
    name: 'status',
  });

  // eslint-disable-next-line consistent-return
  const onSubmit = handleSubmit(async (data) => {
    try {
      await dispatch(
        updateChequeStatus({
          chequeID: currentCheque._id,
          updatingCheque: {
            date: new Date(data.date),
            status: data.status as ChequeStatus,
            holder: data.holder,
          },
        })
      ).unwrap();

      enqueueSnackbar('Cheque status changed successfully');
      navigate(paths.dashboard.cheque.root(data.status));
      reset();
    } catch (e) {
      if (typeof e === 'string') {
        enqueueSnackbar(e, {
          variant: 'error',
        });
      } else {
        enqueueSnackbar(e?.message, {
          variant: 'error',
        });
      }
    }
  });

  return (
    <Dialog fullWidth maxWidth="xs" open={isOpen} onClose={onClose}>
      <DialogTitle sx={{ pb: 2 }}>Change Status</DialogTitle>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogContent sx={{ typography: 'body2' }}>
          <Typography sx={{ mb: 2 }} variant="body2">
            Are you sure want to change the status of the cheque?
          </Typography>

          <Stack spacing={2}>
            <RHFSelect fullWidth name="status" label="Status" required>
              {getNextAvailableStatuses(currentCheque.status).map((type, index) => (
                <MenuItem
                  key={type}
                  value={type}
                  sx={{
                    textTransform: 'capitalize',
                  }}
                >
                  <Label
                    variant="soft"
                    color={
                      (type === ChequeStatus.PAID && 'success') ||
                      (type === ChequeStatus.RETURN && 'error') ||
                      (type === ChequeStatus.SETTLED && 'warning') ||
                      (type === ChequeStatus.RECOVERED && 'success') ||
                      (type === ChequeStatus.HANDOVER && 'info') ||
                      'default'
                    }
                  >
                    {type.split('_').join(' ')}
                  </Label>
                </MenuItem>
              ))}
            </RHFSelect>

            <Controller
              name="date"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DatePicker
                  label="Date"
                  disableFuture
                  value={field.value}
                  onChange={(newValue) => {
                    field.onChange(newValue);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!error,
                      helperText: error?.message,
                    },
                  }}
                />
              )}
            />

            {(watchStatus === ChequeStatus.PAID || watchStatus === ChequeStatus.HANDOVER) && (
              <RHFTextField required name="holder" label="Holder" />
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={onClose}>
            Cancel
          </Button>

          <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
            Update
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}

export default UpdateChequeStatus;
